/**
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsSrc/utils').MoleculeLoadOptions} MoleculeLoadOptions
 * @typedef {import(
 *     'BMapsUI/dialog/ImportPane/ImportProteinPane/Tabs/folding_utils'
 * ).FoldingInputData } FoldingInputData
 * @typedef {import(
 *     'BMapsUI/dialog/ImportPane/ImportProteinPane/Tabs/folding_utils'
 * ).FoldingServiceArgs } FoldingServiceArgs
 * @typedef {import(
 *     'BMapsUI/dialog/ImportPane/ImportProteinPane/Tabs/folding_utils'
 * ).FoldingResults } FoldingResults
 */

import {
    AtomGroupFactory, CaseData, Fragment, DecodedFragment, Compound, CompoundHeritage, structureKey,
} from 'BMapsModel';
import { disallowIfDisconnected } from 'BMapsSrc/server/session_utils';
import * as projectState from 'BMapsRedux/projectState/access';
import { floatEquals } from 'BMapsSrc/math';
import { prepareFoldingArgs } from 'BMapsUI/dialog/ImportPane/ImportProteinPane/Tabs/folding_utils';
import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { ResponseIds } from '../server/server_common';
import { UserCmd, UserActions } from './UserCmd';
import {
    MapCase, PdbImportCase, AlphaFoldImportCase, UserDataImportCase, FoldingDataImportCase,
} from '../model/MapCase';
import { MolDataSource, showAlert } from '../utils';
import { getFullResidueId } from '../util/mol_info_utils';
import {
    integrateCompounds, integrateEnergies, integrateFragments, doWithActiveSolute,
} from './cmds_common';
import { pointDistance } from '../util/atom_distance_utils';
import { alignmentFrameKey, assembleCompoundFrameTransform } from '../model/frameTransform';
import { defaultClashingTolerance, getClashingDistance } from '../util/clash_detection';
import { getPreferences } from '../redux/prefs/access';
import { ensureArray, sleep } from '../util/js_utils';
import { withPausedRedisplay } from '../util/display_utils';
import { getPdbDataForResidues } from '../util/pdb_export';
import { compoundsToMolJson, isClientOnlyExportFormat } from '../util/moljson_export';

/**
 * Identity USAlign transform — used when a structure is already in the active
 * reference frame (a pre-aligned structure of interest, or a load whose structure
 * carries a matching `otherProperties.alignedTo`), so no USAlign is needed.
 */
const IDENTITY_ALIGN_TRANSFORM = {
    rotation: [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
    translation: [0, 0, 0],
};

/**
 * @typedef ConnectedDataCmds
 * @type {object}
 * @property {zapAll} ZapAll
 * @property {zapConnection} ZapConnection
 * @property {fetchMaps} FetchMaps
 * @property {fetchConnectionMaps} FetchConnectionMaps
 * @property {chooseProtein} ChooseProtein
 * @property {loadProteinFromPdb} LoadProteinFromPdb
 * @property {loadMolecule} LoadMolecule
 * @property {loadMolData} LoadMolData
 * @property {selectCmd} Select
 * @property {selectAtom} SelectAtom
 * @property {exportToFormat} ExportToFormat
 * @property {exportQueryToFormat} ExportQueryToFormat
 * @property {exportCompoundsToFormat} ExportCompoundsToFormat
 * @property {exportSelectionToFormat} ExportSelectionToFormat
 * @property {loadStarterCompounds} LoadStarterCompounds
 * @property {updateMolProps} UpdateMolProps
 * @property {removeCompound} RemoveCompound
 * @property {renameCompound} RenameCompound
 * @property {copyCompound} CopyCompound
 * @property {copyResiduesToCompound} CopyResiduesToCompound
 * @property {removeConnection} RemoveConnection
 * @property {alignProtein} AlignProtein
 * @property {addStructureOfInterest} AddStructureOfInterest
 * @property {ensureUnloadedAlignments} EnsureUnloadedAlignments
 * @property {setProteinTarget} SetProteinTarget
 * @property {setAllTargetInfo} SetAllTargetInfo
 * @property {callProteinFolding} CallProteinFolding
 * @property {foldAndLoad} FoldAndLoad
 */

/** @type {ConnectedDataCmds} */
export const ConnectedDataCmds = {
    ZapAll: new UserCmd('ZapAll', zapAll),
    ZapConnection: new UserCmd('ZapConnection', zapConnection),
    FetchMaps: new UserCmd('FetchMaps', fetchMaps),
    FetchConnectionMaps: new UserCmd('FetchConnectionMaps', fetchConnectionMaps),
    ChooseProtein: new UserCmd('ChooseProtein', chooseProtein),
    LoadProteinFromPdb: new UserCmd('LoadProteinFromPdb', loadProteinFromPdb),
    LoadMolecule: new UserCmd('LoadMolecule', loadMolecule),
    LoadMolData: new UserCmd('LoadMolData', loadMolData),
    AssembleCompound: new UserCmd('AssembleCompound', assembleCompound),
    Select: new UserCmd('Select', selectCmd),
    SelectAtom: new UserCmd('SelectAtom', selectAtom),
    ExportToFormat: new UserCmd('ExportToFormat', exportToFormat),
    ExportQueryToFormat: new UserCmd('ExportQueryToFormat', exportQueryToFormat),
    ExportCompoundsToFormat: new UserCmd('ExportCompoundsToFormat', exportCompoundsToFormat),
    ExportSelectionToFormat: new UserCmd('ExportSelectionToFormat', exportSelectionToFormat),
    LoadStarterCompounds: new UserCmd('LoadStarterCompounds', loadStarterCompounds),
    UpdateMolProps: new UserCmd('UpdateMolProps', updateMolProps),
    RemoveCompound: new UserCmd('RemoveCompound', removeCompound),
    RenameCompound: new UserCmd('RenameCompound', renameCompound),
    CopyCompound: new UserCmd('CopyCompound', copyCompound),
    CopyResiduesToCompound: new UserCmd('CopyResiduesToCompound', copyResiduesToCompound),
    RemoveConnection: new UserCmd('RemoveConnection', removeConnection),
    AlignProtein: new UserCmd('AlignProtein', alignProtein),
    AddStructureOfInterest: new UserCmd('AddStructureOfInterest', addStructureOfInterest),
    EnsureUnloadedAlignments: new UserCmd('EnsureUnloadedAlignments', ensureUnloadedAlignments),
    SetProteinTarget: new UserCmd('SetProteinTarget', setProteinTarget),
    SetAllTargetInfo: new UserCmd('SetAllTargetInfo', setAllTargetInfo),
    CallProteinFolding: new UserCmd('CallProteinFolding', callProteinFolding),
    FoldAndLoad: new UserCmd('FoldAndLoad', foldAndLoad),
};

/**
 * @param {MapCase} mapCase
 * @param {{ isTarget: boolean }} targetInfo
 */
function setProteinTarget(mapCase, targetInfo={}) {
    projectState.setProteinTarget(mapCase, targetInfo);
    App.Workspace.rebuildProteinTree(true);
}

/**
 * Add a structure of interest to the project targets WITHOUT keeping it loaded,
 * first computing its alignment to the active reference frame so a later
 * fragment-data query can place its fragments (the "GET" step).
 *
 * An unloaded target sends no transform to the data service (oneCaseExtras), so
 * its fragments can't be placed in the common frame → empty column. To fix that
 * we compute the structure's whole-protein transform to the active reference
 * frame (computeUnloadedAlignment) and store it + which reference it aligned to
 * on the target entry. It is applied at query time only while that reference is
 * still active.
 *
 * The alignment is computed WITHOUT loading the structure into the workspace:
 * USAlign runs on the bfd-server from two PDB strings, so we load the structure
 * server-side only, export its PDB, align, and drop the server solute — the 3D
 * scene and camera are never touched. See computeUnloadedAlignment.
 *
 * If there is no loaded reference at add time, the structure is added with no
 * transform (treated as "unknown frame" — e.g. pre-aligned/precomputed coords).
 *
 * @param {MapCase} mapCase
 * @param {{ isTarget?: boolean }} [opts]
 */
async function addStructureOfInterest(mapCase, { isTarget=true }={}) {
    let alignTransform = null;
    let alignReferenceFrame = null;

    const alignedTo = mapCase.otherProperties?.alignedTo;
    if (alignedTo) {
        // Pre-aligned structure (e.g. a prepared kinase): its fragments are already in
        // the `alignedTo` frame, so record an identity transform and skip the server load.
        alignTransform = IDENTITY_ALIGN_TRANSFORM;
        alignReferenceFrame = alignedTo;
    } else {
        const dataConnection = App.PrimaryDataConnection;
        const refCaseData = App.Workspace.defaultAlignmentReferenceCaseData();
        // Only server / data-service cases can carry fragment data to place, so only they
        // are worth aligning; client-side imports never have data-service fragments.
        const canAlign = refCaseData
            && dataConnection.getMode() === 'server'
            && mapCase.isServerCase
            && refCaseData.getProteinChains().length > 0;
        if (canAlign) {
            try {
                ({ transform: alignTransform, referenceFrame: alignReferenceFrame } = (
                    await computeUnloadedAlignment(mapCase, refCaseData, dataConnection)
                ));
            } catch (e) {
                console.warn(`AddStructureOfInterest: alignment failed for ${mapCase?.uri}: ${e}`);
            }
        }
    }

    projectState.addStructureToProjectTargets(
        mapCase, { isTarget, alignTransform, alignReferenceFrame },
    );
}

/**
 * Decide, without I/O, how to resolve an unloaded structure's alignment to the active reference
 * frame. Pure + exported for testing.
 *  - 'cached'   reuse the entry's stored transform (it still targets the active frame)
 *  - 'identity' the structure is already in the active frame (otherProperties.alignedTo equals
 *               activeFrame) — e.g. a prepared kinase whose panel reference shares its frame
 *  - 'compute'  needs a server USAlign onto the active reference
 * @param {{ alignTransform?: object, alignReferenceFrame?: string }} [entry] the target-list entry
 *   if any — a curated-panel member resolves to its group entry, which carries no transform
 * @param {MapCase} mapCase
 * @param {string} activeFrame structureKey of the active alignment reference
 * @returns {{ kind: 'cached', transform: object } | { kind: 'identity' } | { kind: 'compute' }}
 */
export function planUnloadedAlignment(entry, mapCase, activeFrame) {
    if (entry?.alignTransform && entry.alignReferenceFrame === activeFrame) {
        return { kind: 'cached', transform: entry.alignTransform };
    }
    // Already in the active frame → no alignment needed (identity). A prepared kinase is
    // pre-aligned to its otherProperties.alignedTo frame; ensureUnloadedAlignments sets the active
    // frame to the reference's own alignedTo, so a whole curated panel that shares that frame
    // resolves to identity instead of loading + USAligning each member.
    const alignedTo = mapCase?.otherProperties?.alignedTo;
    if (alignedTo && alignedTo === activeFrame) {
        return { kind: 'identity' };
    }
    // A non-pre-aligned one-off structure of interest still needs a server USAlign onto the
    // active reference.
    return { kind: 'compute' };
}

/**
 * Resolve the whole-protein alignment transform for every unloaded structure in a fragment-data
 * query, keyed by structureKey — so a structure aligns the same way whether it's an individual
 * structure of interest or a curated-panel (group) member (they are all just MapCases). Run as a
 * pre-pass before the query; the returned map drives both the data-service case-extras and the
 * client-side fragment display.
 *
 * Per structure (see planUnloadedAlignment): reuse a still-valid cached entry transform; else an
 * identity transform if it is already in the active frame (its alignedTo equals the reference's
 * frame — a prepared panel shares its reference's frame); else a server USAlign onto the active
 * reference. Loaded structures are skipped (their transform is live on caseData). The
 * individual-entry cache is refreshed when a transform is (re)computed; group members resolve
 * transiently (no entry to persist to), which is fine — reference panels aren't persisted.
 *
 * @param {MapCase[]} mapCases the query's structures
 * @returns {Promise<Map<string, { rotation: number[][], translation: number[] }>>}
 *   structureKey → raw USAlign transform valid for the active reference frame
 */
async function ensureUnloadedAlignments(mapCases=[]) {
    const alignments = new Map();
    const refCaseData = App.Workspace.defaultAlignmentReferenceCaseData();
    // Active frame = the reference's own prepared frame (otherProperties.alignedTo) if it has one,
    // else its structureKey. So a prepared kinase reference and the pre-aligned panel members share
    // a frame (identity for the whole panel) instead of USAligning each member.
    const activeFrame = alignmentFrameKey(refCaseData);
    const dataConnection = App.PrimaryDataConnection;
    if (!refCaseData || !activeFrame || dataConnection.getMode() !== 'server') return alignments;

    const refHasChains = refCaseData.getProteinChains().length > 0;
    const targetInfoMap = projectState.getTargetInfoMap();
    // Per-prepared-frame transform cache: members pre-aligned to the same frame (alignedTo) are
    // superimposed in it, so one transform onto the active reference serves them all — compute it
    // once per frame instead of loading + USAligning every member of a curated panel.
    const frameTransforms = new Map();
    for (const mapCase of mapCases) {
        const structKey = structureKey(mapCase);
        if (!structKey || alignments.has(structKey)) continue; // dedup repeated structures
        if (App.Workspace.lookupCaseData(mapCase)) continue; // loaded → live transform on caseData

        // cached/identity resolve with no I/O and are always safe; only the server USAlign
        // (compute) path is gated on isServerCase + a reference that has protein chains.
        const plan = planUnloadedAlignment(targetInfoMap.get(mapCase), mapCase, activeFrame);
        if (plan.kind === 'cached') {
            alignments.set(structKey, plan.transform);
        } else if (plan.kind === 'identity') {
            alignments.set(structKey, IDENTITY_ALIGN_TRANSFORM);
        } else if (mapCase.isServerCase && refHasChains) {
            const alignedTo = mapCase.otherProperties?.alignedTo;
            if (alignedTo && frameTransforms.has(alignedTo)) {
                // Reuse the transform already computed for this prepared frame — one USAlign serves
                // the whole panel (members are superimposed in their common frame).
                alignments.set(structKey, frameTransforms.get(alignedTo));
                continue;
            }
            // Server USAlign onto the active reference (same path as add time), cached by frame.
            try {
                const { transform, referenceFrame } = await computeUnloadedAlignment(
                    mapCase, refCaseData, dataConnection,
                );
                if (transform) {
                    alignments.set(structKey, transform);
                    if (alignedTo) frameTransforms.set(alignedTo, transform);
                    // Keep the individual-entry cache warm (no-op for group members).
                    projectState.updateStructureAlignment(
                        mapCase, { alignTransform: transform, alignReferenceFrame: referenceFrame },
                    );
                }
            } catch (e) {
                console.warn(`ensureUnloadedAlignments: alignment failed for ${mapCase?.uri}: ${e}`);
            }
        }
    }
    return alignments;
}

/**
 * @typedef {{ isTarget: boolean }} targetInfo
 * @typedef {{ mapCase: MapCase, targetInfo: { isTarget: boolean } }} mapCaseInfo
 * @param {mapCaseInfo[] | mapCaseInfo} mapCaseInfoList
 */
function setAllTargetInfo(mapCaseInfoList) {
    projectState.setAllTargetInfo(mapCaseInfoList);
    App.Workspace.rebuildProteinTree(true);
}

async function zapAll(usePromise=true) {
    // Before the first await, for the same reason as caseRemovalRequested in
    // DisplayCmds.removeCase: the 'zapAll' published by Workspace.zap below comes only after
    // the server has confirmed, which is behind any long command already running.
    EventBroker.publish('zapRequested');
    const [primary, ...otherDataConnections] = App.DataConnections;
    await Promise.all([
        zapConnection(primary, usePromise),
        ...(otherDataConnections.map((dc) => App.ConnectionManager.removeDataConnection(dc))),
    ]);
    App.Workspace.zap();
}

function zapConnection(dataConnection, usePromise=true) {
    return dataConnection.zap(usePromise);
}

async function fetchMaps() {
    const mapLists = await Promise.all(
        App.DataConnections.map((dc) => fetchConnectionMaps(dc))
    );
    return mapLists.flat();
}

// How many times to (re)request the map list, and how long to wait between tries.
// On a dropped socket the in-flight list-maps is rejected (server_responders
// rejectAll) so the await below throws instead of hanging. Because loadMapData
// runs only after a *successful* await, a failed attempt loads nothing, which
// makes re-requesting duplicate-free.
// (Refreshing an *already-loaded* list is a separate feature — deferred-issues #3
// piece 2 — because addMapCase does not dedupe.)
const LIST_MAPS_MAX_ATTEMPTS = 3;
const LIST_MAPS_RETRY_DELAY_MS = 2000;
// How long to wait for the socket to come back before giving up. Reconnecting costs a
// ServerMonitor tick (2s poll), a hasSlot round trip and a websocket handshake — all over
// the network that just failed — so this is far longer than the between-tries delay.
const LIST_MAPS_RECONNECT_WAIT_MS = 30000;

/**
 * Resolve true once this connector reports itself connected again, or false if it has not
 * come back within timeoutMs. The session survives a brief drop (ensureConnection reconnects
 * to the same slot), but the in-flight command does not: its response was written to the
 * dead socket and nothing replays it. So the recovery is to re-ask once we are back.
 * @param {*} connector
 * @param {number} timeoutMs
 * @returns {Promise<boolean>}
 */
function waitForReconnect(connector, timeoutMs) {
    return new Promise((resolve) => {
        let timer = null;
        let onStatusChanged = null;
        const done = (reconnected) => {
            clearTimeout(timer);
            EventBroker.unsubscribe('connectionStatusChanged', onStatusChanged);
            resolve(reconnected);
        };
        onStatusChanged = (evtName, { connector: which, connected }) => {
            if (which === connector && connected) done(true);
        };
        timer = setTimeout(() => done(false), timeoutMs);
        EventBroker.subscribe('connectionStatusChanged', onStatusChanged);
    });
}

/**
 * Wait before the next list-maps attempt. If the socket is up this is a plain backoff; if it
 * is down, wait for the reconnect instead. Retrying while disconnected is wasted: ws.send()
 * throws immediately when the socket isn't open (no readyState guard in BrowserWebSocketConn),
 * so a fixed delay burns every remaining attempt seconds before the connection returns.
 * Returns false when the connection never came back, so the caller reports instead of spinning.
 * @param {*} connector
 * @returns {Promise<boolean>} whether it is worth trying again
 */
async function readyToRetryListMaps(connector) {
    if (connector.isConnected()) {
        await sleep(LIST_MAPS_RETRY_DELAY_MS);
        return true;
    }
    return waitForReconnect(connector, LIST_MAPS_RECONNECT_WAIT_MS);
}

async function fetchConnectionMaps(dataConnection) {
    const { connector, caseDataCollection } = dataConnection.get();
    for (let attempt = 1; attempt <= LIST_MAPS_MAX_ATTEMPTS; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop -- attempts are inherently sequential
            const responseData = await connector.cmdListMaps();
            const allMapData = responseData.byId(ResponseIds.MapList);
            for (const mapData of allMapData) {
                caseDataCollection.loadMapData(mapData);
            }
            return caseDataCollection.getMapList();
        } catch (ex) {
            const detail = ex?.message ?? ex;
            console.warn(`fetchConnectionMaps: list-maps failed (attempt ${attempt}/${LIST_MAPS_MAX_ATTEMPTS}): ${detail}`);
            // eslint-disable-next-line no-await-in-loop -- deliberate wait between retries
            const willRetry = attempt < LIST_MAPS_MAX_ATTEMPTS
                && await readyToRetryListMaps(connector);
            if (!willRetry) {
                // Out of attempts, or the socket never came back. Surface it the same way as
                // a server-side list-maps error (reportListMapsError, project_data.js)
                // instead of leaving a bare unhandled rejection.
                showAlert('Load BMaps Data', [
                    'An error occurred while fetching Boltzmann Maps data.',
                    'Some data may not be available.',
                    'Please <a href="mailto:support@coniferpoint.com">contact us</a> if this problem persists.',
                ]);
                return caseDataCollection.getMapList();
            }
        }
    }
    return caseDataCollection.getMapList();
}

/**
 * @param {FoldingServiceArgs} foldingData
 * @param {*} handlers
 * @param {*} dataConnection
 * @returns {FoldingResults}
 */
async function callProteinFolding(foldingData, handlers, dataConnection=App.PrimaryDataConnection) {
    const { connector } = dataConnection.get();
    const responder = connector.cmdFoldProtein({
        foldingProgram: foldingData.foldingProgramId,
        foldingArgs: foldingData.foldingArgs,
    });
    const responseData = await responder.handleResponses(handlers);
    const errors = responseData.errors;
    const results = responseData.byId(ResponseIds.FoldResults);
    // results is the list of all FoldResults packets received, each packet a list of fold results
    const { firstPacket, firstFoldResult } = getFirstFoldResult(results);
    function getFirstFoldResult(foldingResultPackets) {
        // Extract the first populated fold results we see (should be the first, but can fall back)
        for (const packet of foldingResultPackets) {
            for (const foldResult of packet) {
                if (foldResult.data) {
                    return { firstPacket: packet, firstFoldResult: foldResult };
                } else {
                    console.warn('Empty fold results received.');
                }
            }
        }
        return { firstPacket: null, firstFoldResult: null };
    }

    if (!firstFoldResult && errors.length === 0) {
        errors.push('No data returned by the folding service, which usually indicates a failure to load the molecules. Please double-check your inputs (especially ligands) and try again.');
    }

    if (results.length > 1 || firstPacket?.length > 1) {
        console.warn("I don't yet know how to deal with multiple fold results at once. Just using the first one.");
    }

    return {
        data: firstFoldResult?.data,
        format: firstFoldResult?.format,
        resultProperties: firstFoldResult?.resultProperties,
        errors,
    };
}

/**
 * Call protein folding service and load the results into the workspace.
 * This doesn't get user conformation to Zap before loading, so not intended for GUI use.
 * @param {FoldingInputData} foldingInput folding input data
 * @param {object?} loadOptionsIn Optional load args to pass into ChooseProtein.
 * @returns { Promise<{
 *      foldResults: { data: string, format: string, errors: string[] },
 *      loadResults?: {
 *          caseData?: import('BMapsModel').CaseData,
 *          mapCase?: MapCase,
 *          errors: string[],
 *          warnings?: string[],
 *          energies?: object[],
 *      }
 * }> }
 */
async function foldAndLoad(foldingInput, loadOptionsIn={}) {
    const {
        foldingRequestArgs, errors: prepErrors, loadOptions: prepLoadOptions,
    } = await prepareFoldingArgs(foldingInput);

    if (prepErrors && prepErrors.length > 0) {
        return { foldResults: { errors: prepErrors } };
    }

    const foldResults = await UserActions.CallProteinFolding(foldingRequestArgs);
    if (foldResults.errors.length > 0) {
        return { foldResults };
    }

    const loadOptions = { ...prepLoadOptions, ...loadOptionsIn };

    const loadResults = await UserActions.ChooseProtein(new FoldingDataImportCase({
        foldingData: foldingRequestArgs,
        data: foldResults.data,
        format: foldResults.format,
        resultProperties: foldResults.resultProperties,
        loadOptions,
    }), loadOptions);

    return { foldResults, loadResults };
}

/**
 * Load a structure from the RCSB PDB by its bare four-character id.
 *
 * This is the callee of the Gibbs `load-protein-from-pdb` action, whose two producers -- the
 * planner and macro replay -- both emit the bare id. The prefix that makes it a chooseProtein
 * URI is added here, at the action boundary, so chooseProtein keeps its URI contract and a bare
 * string never has to be shape-sniffed there. The id check is main's own PdbImportCase.isValidId;
 * without it "test" or "1234" would go to the PDB as a structure request.
 * See docs/gibbs-migration-register.md, Slice D item 1.
 *
 * @param {string} pdbId a digit followed by three alphanumerics, at least one of them a letter
 * @param {{ keepExisting?: boolean }} [loadOptions] as for chooseProtein
 * @param {*} [dataConnection] as for chooseProtein
 * @returns {ReturnType<typeof chooseProtein>}
 */
async function loadProteinFromPdb(pdbId, loadOptions={}, dataConnection=App.PrimaryDataConnection) {
    const id = typeof pdbId === 'string' ? pdbId.trim().toUpperCase() : '';
    if (!PdbImportCase.isValidId(id)) {
        return { errors: [`Not a PDB id: ${JSON.stringify(pdbId)}`] };
    }
    return chooseProtein(`pdb:${id}`, loadOptions, dataConnection);
}

/**
 * @param {string | MapCase} caseInfoIn
 * @param {{
 *      keepExisting: boolean
 * }} loadOptions
 * @param {*} dataConnection
 * @returns {Promise<{
 *      errors: string[]
 *      warnings?: string[]
 *      mapCase?: MapCase
 *      caseData?: import('BMapsModel').CaseData
 *      energies?: energies
 * }>}
 */
async function chooseProtein(caseInfoIn, loadOptions={}, dataConnection=App.PrimaryDataConnection) {
    if (disallowIfDisconnected('Switching proteins', dataConnection.connector)) {
        return { errors: ['Action not allowed in disconnected session'] };
    }

    if (!loadOptions.keepExisting) {
        await zapAll();
    }

    /**
     * @type {import('BMapsModel').DataConnection}
     */
    const { connector, caseDataCollection } = dataConnection.get();
    let caseInfo = caseInfoIn;

    if (typeof (caseInfoIn) === 'string') {
        const uri = caseInfoIn;
        caseInfo = caseDataCollection.findMapByUri(uri);

        if (!caseInfo) {
            if (PdbImportCase.isUri(uri)) {
                caseInfo = new PdbImportCase(PdbImportCase.parseUri(uri).id);
            } else if (AlphaFoldImportCase.isUri(uri)) {
                caseInfo = new AlphaFoldImportCase(AlphaFoldImportCase.parseUri(uri).id);
            }
        }
    }

    if (caseInfo == null) {
        const msg = 'Map case not found';
        return { errors: [msg] };
    }

    const uri = caseInfo.uri;
    const loadOptionsForServer = serverLoadOptions(caseInfo, loadOptions);
    let responseData;
    if (caseInfo instanceof PdbImportCase || caseInfo instanceof AlphaFoldImportCase) {
        responseData = await withPausedRedisplay(
            connector.cmdLoadPdbId({
                pdbId: caseInfo.pdbID,
                loadOptions: loadOptionsForServer,
            }),
        );
    } else if (caseInfo instanceof UserDataImportCase) {
        responseData = await withPausedRedisplay(connector.cmdLoadPdbString({
            data: caseInfo.data,
            pdbId: caseInfo.pdbID,
            moleculeName: caseInfo.molecule_name,
            fileType: caseInfo.data_format,
            loadOptions: loadOptionsForServer,
        }));
    } else if (caseInfo instanceof FoldingDataImportCase) {
        responseData = await withPausedRedisplay(connector.cmdLoadPdbString({
            data: caseInfo.data,
            fileType: caseInfo.data_format,
            moleculeName: caseInfo.molecule_name,
            loadOptions: loadOptionsForServer,
        }));
    } else {
        responseData = await withPausedRedisplay(
            connector.cmdGetCaseFiles(uri, loadOptionsForServer)
        );
    }

    // A map case belongs to the collection it is loaded into. Rebind unconditionally: a stale
    // reference left by a torn-down connection (e.g. a released selectivity session) must not
    // shadow the current collection, or lookupCaseData resolves against a dead one and the
    // freshly-loaded protein reads as "not loaded".
    caseInfo.setCaseDataCollection(caseDataCollection);

    // Get alignment reference before actually loading the incoming structure into the workspace.
    // If chooseProtein can ever load multiple cases at once, we'd probably want to call
    // defaultAlignmentReferenceCaseData before loading each individual item (not just once at the
    // beginning), so that if one of them has chains and should become the reference, it will be
    // used as the reference for the others.
    const alignRefCaseData = App.Workspace.defaultAlignmentReferenceCaseData();
    const result = handleProteinResponses(caseInfo, responseData);
    const { errors, caseData: loadedCaseData } = result;
    applyCaseProperties(loadedCaseData);

    if (errors.length > 0) {
        console.log(`Errors loading protein ${caseInfo}: ${errors}`);
    }

    // Ligand's own constructor unconditionally records a 'Co-Crystal Ligand' heritage
    // entry (it has no way to know its actual origin) - wrong for anything folded, since
    // nothing here was crystallized. Correct it for every fold, not just screening-
    // derived ones (this mislabeling predates screening entirely) - a screening ligand is
    // still just a folded ligand, so a single 'Co-Folded' label is correct and sufficient
    // for both; no need to distinguish screening from a standalone fold here.
    if (errors.length === 0 && loadedCaseData && caseInfo instanceof FoldingDataImportCase) {
        for (const ligand of loadedCaseData.getLigands()) {
            ligand.setHeritage([]);
            CompoundHeritage.addFoldedLigand(ligand, caseInfo.foldingData);
        }
    }

    // Need to add the case to the map selector if we don't already have it
    if (errors.length === 0 && !caseDataCollection.findMapByCase(caseInfo)) {
        caseDataCollection.addMapCase(caseInfo);
    }

    App.Workspace.rebuildProteinTree(false);
    EventBroker.publish('dataLoaded');
    EventBroker.publish('proteinLoaded', { mapCase: caseInfo, caseData: loadedCaseData });
    UserActions.RedrawScene({ forceProteinView: true, recenter: true });

    if (loadedCaseData) {
        // A lot of cases for fragment loading here, between fragmentLoading and loadOptions flags.
        // For this, fragmentLoading is sent to the server, loadOptions flags are client=side.
        // Cases:
        // * skip - Don't load fragments, and don't expect to (Should probably be in loadOptions)
        //          Also skips the fetch of fragment set data from fragserv API
        //          Fragments Selector will not indicate "determining available fragments"
        // * isServerCase (builtin or user structure)
        //   * deferFragments - Don't attempt to load fragments now, someone else will handle later.
        //   * waitForFragments - request fragments and block until they complete
        //   * regular server case - request fragments, but don't block
        // * not a server case: no fragments available, but fetch fragset info from fragserv.
        if (loadOptionsForServer.fragmentLoading === 'skip') {
            App.Workspace.setAvailableFragments(caseInfo, []);
        } else if (caseInfo.isServerCase) {
            // Loading several cases in a row: when deferred, the caller refreshes fragments
            // for the eligible ones itself, one at a time, instead of letting them fire here
            // unawaited and race each other across cases (BP2-2623).
            if (!loadOptions.deferFragments) {
                if (loadOptions.waitForFragments) {
                    await UserActions.RefreshFragmentInfo(caseInfo, loadOptions.fragmentLoading);
                } else {
                    UserActions.RefreshFragmentInfo(caseInfo, loadOptions.fragmentLoading);
                }
            }
        } else { // normal load of case without frags
            App.Workspace.setAvailableFragments(caseInfo, []);
            App.Workspace.refreshFragservInfo(); // Don't await
        }
    }

    const canAlignInPreviewMode = false;
    const canAlign = (dataConnection.getMode() === 'server' || canAlignInPreviewMode)
        && alignRefCaseData?.getProteinChains().length > 0
        && loadedCaseData?.getProteinChains().length > 0;
    if (loadOptions.keepExisting && canAlign) {
        await alignProtein({ refCaseData: alignRefCaseData, alignCaseData: loadedCaseData });
        UserActions.RedrawScene({ forceProteinView: true, recenter: true });
        // Why reset display twice?
        // The reset above will show the new protein in its original coordinate system.
        // This one here will switched to the aligned coordinates.
        // For better or worse, this indicates visually that an alignment occurred.
    }
    return result;
}

/**
 * Align one protein to another. This does two things:
 * 1. Notes the reference protein on the aligned protein.
 * 2. Calculates the transform and assigns the "whole protein transform."
 *
 * Note: binding site transforms are handled separately. They make use of the reference protein
 * but calculate a local transform just for the binding site.
 * @param {CaseData} inboundCaseData
 */
async function alignProtein(options) {
    if (options.alignCaseData === options.refCaseData) return;

    // Pre-aligned structure already in the reference frame: record identity + reference and
    // skip the USAlign round-trip. Mirrors the unloaded structure-of-interest path.
    const alignedTo = options.alignCaseData.mapCase?.otherProperties?.alignedTo;
    // Two structures prepared to the same frame are identity to each other (alignmentFrameKey
    // collapses a prepared structure to its alignedTo frame).
    const refFrame = alignmentFrameKey(options.refCaseData);
    if (alignedTo && alignedTo === refFrame) {
        options.alignCaseData.setReferenceCaseData(options.refCaseData);
        options.alignCaseData.setWholeProteinTransform(IDENTITY_ALIGN_TRANSFORM);
        return;
    }

    const alignMatrix = await withPausedRedisplay(getAlignmentMatrix(options));
    if (alignMatrix) {
        const alignmentFailed = alignMatrix.rotation.flat().every((x) => floatEquals(x, 0));
        if (alignmentFailed) {
            const alignName = options?.alignCaseData.getName();
            const refName = options?.refCaseData.getName();
            console.warn(`Protein alignment failed for aligning ${alignName} to ${refName}`);
        } else {
            const { alignCaseData, refCaseData }= options;
            alignCaseData.setReferenceCaseData(refCaseData);
            alignCaseData.setWholeProteinTransform(alignMatrix);
            UserActions.RedrawScene({ recenter: true });
        }
    } else {
        console.error('No matrix reported after align-protein cmd');
    }
}

/**
 * Calculate an alignment transformation between two proteins.
 * @param {{
*      refCaseData: CaseData,
*      alignCaseData: CaseData,
*      refSpec: string,
*      alignSpec: string,
* }} param0
* @returns {Promise<{ translation: [number,number,number], rotation: [number,number,number][]}>}
*/
async function getAlignmentMatrix({
    refCaseData, alignCaseData, refSpec, alignSpec,
}) {
    const refSelection = refSpec || defaultAlignmentSelection(refCaseData);
    const alignSelection = alignSpec || defaultAlignmentSelection(alignCaseData);

    const { connector: refConnector } = App.getDataParents(refCaseData);
    const { data: pdbRef } = await UserActions.ExportQueryToFormat(refSelection, 'pdb', refCaseData);
    const { data: pdbAlign } = await UserActions.ExportQueryToFormat(alignSelection, 'pdb', alignCaseData);
    const reqObj = { pdbRef, pdbAlign };
    const responseData = await refConnector.cmdAlignProtein(reqObj);
    const [alignMatrix] = responseData.byId(ResponseIds.ProteinRotationMatrix);
    return alignMatrix?.result;
}

/**
* For default alignment, select the first chain.
*/
function defaultAlignmentSelection(caseData) {
    const firstChainAtomGroup = caseData.getProteinChains()[0];
    const chain = firstChainAtomGroup.chain;
    return `(protein or dna or rna) and chain ${chain}`;
}

/**
 * @description Convert client-side load options to server-side
 * @param string uri
 * @param {*} loadOptions
 */
function serverLoadOptions(caseInfo, loadOptions) {
    const args = {};
    const { FragmentLoading, Forcefield } = getPreferences();

    if (caseInfo instanceof PdbImportCase
        || caseInfo instanceof AlphaFoldImportCase
        || caseInfo instanceof UserDataImportCase
        || caseInfo instanceof FoldingDataImportCase) {
        // postprocessing: "just_load" | "just_hydrogens" | "load_and_type" | "all" | undefined
        // nameMappings, ligandSmiles: pass through as a string
        args.postprocessing = loadOptions.preserveHydrogens ? 'load_and_type' : 'all';
        args.nameMappings = loadOptions.nameMappings;
        args.ligandSmiles = loadOptions.ligandSmiles;
        if (loadOptions.biologicalAssembly) { // string type, not number
            args.biologicalAssembly = loadOptions.biologicalAssembly;
        }
    }

    args.fragmentLoading = loadOptions.fragmentLoading || FragmentLoading;
    args.forcefield = loadOptions.forcefield || Forcefield;
    return args;
}

/**
 * Process various packets in the responseData in response to get-case-files or load-pdb-id
 * @param {MapCase} mapCase
 * @param {import('BMapsSrc/server_responders').ResponseData} responseData
 */
function handleProteinResponses(mapCase, responseData) {
    const [solute] = responseData.byId(ResponseIds.Solute);
    /** @type {string[]} */
    const errors = responseData.byId(ResponseIds.Error);
    /** @type {string[]} */
    const warnings = responseData.byId(ResponseIds.Warning);
    const energies = [];
    let caseData;

    if (solute) {
        const { caseDataCollection } = App.getDataParents(mapCase);
        // First handle protein, compounds, energies, pdb info
        const {
            atoms, bonds, helices, sheets,
        } = solute.atomData;
        caseData = App.Workspace.addProtein(mapCase, {
            atomGroups: solute.allAtomGroups, soluteName: solute.soluteName, caseDataCollection,
        });
        integrateCompounds(responseData, caseData);
        AtomGroupFactory.PrintSummary(caseData.atomGroups);
        if (solute.compounds) {
            App.Workspace.activateFirstOf(solute.compounds);
            const MAX_COMPOUNDS_FOR_CLASH_DETECTION = 100;
            if (solute.compounds.length <= MAX_COMPOUNDS_FOR_CLASH_DETECTION) {
                reportClashingCompounds(solute.compounds, { alertParams: { dontAlert: true } });
            } else {
                console.log(`Skipping clash detection since ${solute.compounds.length} ligands > ${MAX_COMPOUNDS_FOR_CLASH_DETECTION}`);
            }
        }

        energies.push(...integrateEnergies(responseData, caseData));

        const [pdbInfo] = responseData.byId(ResponseIds.PdbInfo);
        if (pdbInfo) {
            applyPdbInfo(mapCase, pdbInfo);
        }

        integrateFragments(responseData, caseData);

        // Sample compounds
        const starterAvailability = responseData.byId(ResponseIds.StarterAvailability);
        if (starterAvailability.length > 0) {
            const availability = starterAvailability[0];
            if (availability.available) {
                caseData.getSampleCompoundInfo().setAvailable();
            }
        }

        // Water map: the server no longer derives one while loading the case, because that
        // took minutes with nothing shown. It reports whether this case has water simulation
        // data; the client decides a map is derivable when that holds and no computed waters
        // came with the case. Absent (older server) leaves the flag false, so nothing is
        // offered and the user sees the same alert as before.
        const [watermapAvailability] = responseData.byId(ResponseIds.WatermapAvailability);
        if (watermapAvailability) {
            caseData.setHasWaterData(!!watermapAvailability.hasWaterData);
        }
    } else {
        errors.push(`No solute data reported for protein case ${mapCase.projectCase || mapCase.displayName}`);
    }

    return {
        errors, warnings, energies, mapCase, caseData,
    };
}

function applyPdbInfo(caseInfo, pdbInfo) {
    // Update molecule name if it's empty or default.
    if (!caseInfo.molecule_name || caseInfo.hasDefaultMoleculeName()) {
        // mmCIF encodes "no value applicable" as ".", and some bfd-server builds hand
        // that token back verbatim as the molecule name. Treat a lone "." as absent so
        // we fall back to a real name rather than displaying ".".
        const cifName = pdbInfo.molecule_name === '.' ? '' : pdbInfo.molecule_name;
        const pdbMolName = cifName || pdbInfo.gene_name;
        // If the pdb data didn't have a name, use the default
        caseInfo.molecule_name = pdbMolName || caseInfo.molecule_name
            || UserDataImportCase.DefaultRootMolName;
    }
    if (!caseInfo.gene_name) caseInfo.gene_name = pdbInfo.gene_name;
    if (!caseInfo.description) caseInfo.description = pdbInfo.title;
}

// UserActions.LoadMolecule
/**
 * @param {string} molId
 * @param {string} molFormat
 * @param {string} molData
 * @param {MoleculeLoadOptions} moleculeLoadOptions
 * @param {CaseData} caseData
 */
async function loadMolecule(molId, molFormat, molData, moleculeLoadOptions,
    caseData=App.Workspace.firstCaseData()) {
    const source = new MolDataSource({ molFormat, molData });
    if (molId) source.compoundName = molId;
    return loadMolData(source, moleculeLoadOptions, caseData);
}

/**
 * @param {MolDataSource} molSource
 * @param {MoleculeLoadOptions} moleculeLoadOptions
 * @param {CaseData} caseData
 * @returns
 */
async function loadMolData(
    molSource, moleculeLoadOptions, caseData=App.Workspace.firstCaseData(), otherOptions={},
) {
    const { MaxMolecules, Forcefield } = getPreferences();
    const { connector } = App.getDataParents(caseData);
    molSource.maxMolecules = otherOptions.maxMolecules || MaxMolecules || 100;
    if (Forcefield) moleculeLoadOptions.addOptions({ forcefield: Forcefield });
    const responseData = await doWithActiveSolute(
        caseData, () => connector.cmdLoadMolData(molSource, moleculeLoadOptions)
    );
    /** @type {Compound[]} */
    const loadedCompounds = responseData.byId(ResponseIds.Compound);
    /** @type {string[]} */
    const loadErrors = responseData.byId(ResponseIds.Error);
    /** @type {string[]} */
    const loadWarnings = responseData.byId(ResponseIds.Warning);
    App.Workspace.addReplaceCompounds(loadedCompounds, caseData);
    const integrateOptions = { skipProperties: otherOptions.skipProperties };
    integrateCompounds(responseData, caseData, integrateOptions);
    if (!moleculeLoadOptions.isPlaceOutside()) {
        handleClashingCompounds(loadedCompounds);
    }
    App.Workspace.activateFirstOf(loadedCompounds);
    let heritageParent = null;
    if (molSource.sourceCmpds) {
        // Copy To Protein
        const sourceCmpds = [...molSource.sourceCmpds];
        for (const cmpd of loadedCompounds) {
            const sourceIndex = sourceCmpds.findIndex((srcC) => Compound.matches3d(srcC, cmpd));
            if (sourceIndex < 0) {
                console.warn(`Did not find a match for loaded cmpd ${cmpd.resSpec}`);
                continue;
            }
            const [sourceCmpd] = sourceCmpds.splice(sourceIndex, 1);
            heritageParent = sourceCmpd;
            cmpd.copyScopedPropertiesFrom(sourceCmpd, {
                skipPerCompoundTarget: cmpd.caseData !== sourceCmpd.caseData,
                operation: cmpd.caseData !== sourceCmpd.caseData ? 'copy to protein' : 'duplicate',
            });
        }
    } else if (molSource.modifiedFrom) {
        heritageParent = caseData.getCompoundBySpec(molSource.modifiedFrom);
        if (!heritageParent) {
            heritageParent = App.Workspace.getCompoundBySpec(molSource.modifiedFrom);
        }
    }
    if (!otherOptions.skipHeritage) {
        CompoundHeritage.addMolSource(heritageParent, loadedCompounds, molSource);
    }
    return { compounds: loadedCompounds, errors: loadErrors, warnings: loadWarnings };
}

/**
 * Invoke assemble-compound bfd-server API
 */
async function assembleCompound(modificationsIn, caseData) {
    const modifications = Array.isArray(modificationsIn) ? modificationsIn : [modificationsIn];
    // Validate before dereferencing into getAssembleCompoundMolDesc. Without
    // this guard, a missing modMol throws "Cannot read properties of undefined
    // (reading 'resSpec')" deep inside the spec builder, which is opaque to
    // the LLM retry loop and to the user. assemble-compound requires a
    // concrete fragment to attach — if the caller doesn't have one, this is
    // the wrong action (consider grow-from-all-atoms or the fragment-grow
    // shortcut for lead-optimization flows).
    for (let i = 0; i < modifications.length; i++) {
        const mod = modifications[i];
        if (!mod || !mod.modMol) {
            throw new Error(
                `assemble-compound: modifications[${i}] is missing modMol `
                + '(the fragment or molecule being attached). This action '
                + 'requires a concrete fragment; it cannot assemble from a '
                + 'description alone. For lead design / optimization, use '
                + 'grow-from-all-atoms or start with hit discovery.'
            );
        }
    }
    const { connector } = App.getDataParents(caseData);
    let assembleSpec;
    try {
        assembleSpec = getAssembleCompoundSpec(modifications, caseData);
    } catch (e) {
        // e.g. a cross-structure fragment with no shared alignment reference
        // (assembleCompoundFrameTransform). Surface it and abort rather than place the
        // compound with the wrong coordinates.
        showAlert('Cannot add fragment', e.message);
        return { compounds: [], errors: [e.message] };
    }
    const responseData = await connector.cmdAssembleCompound(assembleSpec);
    const loadedCompounds = responseData.byId(ResponseIds.Compound);
    const loadErrors = responseData.byId(ResponseIds.Error);
    const loadWarnings = responseData.byId(ResponseIds.Warning);
    App.Workspace.addReplaceCompounds(loadedCompounds, caseData);
    integrateCompounds(responseData, caseData);
    handleClashingCompounds(loadedCompounds);
    App.Workspace.activateFirstOf(loadedCompounds);
    // TODO: add mol heritage
    return { compounds: loadedCompounds, errors: loadErrors, warnings: loadWarnings };
}

/**
 * Return json spec for a simple molecule description. Atom coordinates are expressed in
 * `targetCaseData`'s original frame so a fragment picked from a different (aligned) structure
 * is assembled in the same frame as the target it's added to -- otherwise it lands in its
 * source structure's pocket.
 */
function getAssembleCompoundMolDesc(mol, targetCaseData) {
    let name = mol.resSpec;
    if (mol instanceof DecodedFragment) name = mol.baseFrag.name;
    else if (mol instanceof Fragment) name = mol.fragmentName;

    // Map the mol into the target case's original frame (no-op when already there). A null
    // transform leaves coordinates original, matching the previous useOriginal behavior.
    const transform = assembleCompoundFrameTransform(mol, targetCaseData);

    const ret = {
        name,
        atoms: mol.getAtoms().map((atom) => ({
            atom: atom.atom,
            pos: atom.getPosition({ transform }),
        })),
        bonds: mol.getBonds().map(({ atom1, atom2, orderOriginal }) => ({
            fromAtom: atom1.atom, toAtom: atom2.atom, order: orderOriginal,
        })),
    };
    return ret;
}

/**
 * Return a modification spec for AssembleCompound.
 * modMol is the fragment
 * srcMol is the compound to merge the fragment into
 * modInfo contains type, and atom names:
 *   srcAttachAtom
 *   srcDirectionAtom
 *   modAttachAtom
 *   modDirectionAtom
 */
function getAssembleCompoundSpec(modifications, caseData) {
    const assemblies = modifications.map(({ srcMol, modMol, modInfo }) => ({
        srcMolecule: srcMol ? getAssembleCompoundMolDesc(srcMol, caseData) : null,
        modification: {
            modMolecule: getAssembleCompoundMolDesc(modMol, caseData),
            ...modInfo, // type, srcAttachAtom,srcDirectionAtom, modAttachAtom, modDirectionAtom
        },
    }));
    return { assemblies };
}

function handleClashingCompounds(compounds) {
    return reportClashingCompounds(compounds, {
        alertParams: {
            singularNoun: 'imported compound',
            pluralNoun: 'imported compounds',
            some: 'Some',
        },
    });
}
/**
 * @description Check a compound or an array of compounds for steric clashes, and
 * show an alert and log the clashes.
 * Updates energy info for the clashing compounds
 * @param {Compound|Compound[]} compounds
 * @param { {
 *   clashOptions: {
 *     tolerance: number, includeH: boolean, reportAll: boolean
 *   },
 *   alertParams: {
 *     singularNoun: string, pluralNoun: string, some: string, alertTitle: string
 *   },
 * }} options Configuration for the clash detection and the alert text
 * @return { Compound[] } Array of the clashing compounds
 */
export function reportClashingCompounds(compounds, { clashOptions: options, alertParams }={}) {
    const { compoundMap, clashOptions } = App.Workspace.detectClashes(compounds, options);
    const clashingCmpds = [...compoundMap.keys()];
    if (clashingCmpds.length === 0) {
        return clashingCmpds;
    }

    clashAlert(clashingCmpds, compounds, alertParams);
    console.log(getExtraInfoOnClashing(compoundMap, clashOptions));

    // Set warning in Energy Table
    clashingCmpds.forEach((c) => c.energyInfo.setClashing());
    EventBroker.publish('energyCalc');
    return clashingCmpds;
}

/**
 * @description Display clash alert to user
 * @param {Compound[]} clashingCmpds the compounds that are clashing with the protein
 * @param {Compound[]} allCmpds all compounds that were checked for clashes
 * @param { {
 *   singularNoun: string, pluralNoun: string, some: string, alertTitle: string
 *   }} alertParams Configuration for the alert text
 */
function clashAlert(clashingCmpds, allCmpds, alertParams={}) {
    const {
        singularNoun='compound',
        pluralNoun='compounds',
        some='Some of the',
        alertTitle='Steric Clash',
        dontAlert=false,
    } = alertParams;
    if (dontAlert) return;
    const clashingNames = clashingCmpds.map((cmpd) => cmpd.resSpec);
    const msgAlert = [];
    if (clashingCmpds.length > 1) {
        msgAlert.push(
            `${some} ${pluralNoun} are clashing with the protein. You may need to dock them to get favorable energy scores.`,
            `Clashing compounds: ${clashingNames.join(', ')}`,
        );
    } else if (allCmpds.length === 1) {
        msgAlert.push(
            `The ${singularNoun} is clashing with the protein. You may need to dock it to get a favorable energy score.`,
            `Clashing compound: ${clashingNames.join(', ')}`
        );
    } else {
        msgAlert.push(
            `One of the ${pluralNoun} is clashing with the protein. You may need to dock it to get a favorable energy score.`,
            `Clashing compound: ${clashingNames.join(', ')}`
        );
    }
    showAlert(alertTitle, msgAlert);
}

/**
 * Takes array of clashingCmpds and returns a string of the name of the compound(s) and
 * first atoms that are clashing for each compound
 * @param {Compound[]} clashingCmpds
 * @returns String
 */
function getExtraInfoOnClashing(clashReport, { tolerance=defaultClashingTolerance }={}) {
    const clashingExtraInfo = [];
    for (const [cmpd, clashingAtoms] of clashReport) {
        const atomResMaps = new Map(); // Map<atom1, Map<resSpec:string, atomNames:string[]>>
        for (const { atom1, atom2 } of clashingAtoms) {
            const resSpec = getFullResidueId(atom2);
            if (!atomResMaps.get(atom1)) atomResMaps.set(atom1, new Map()); // ensure entry exists
            const resMap = atomResMaps.get(atom1);
            if (!resMap.get(resSpec)) resMap.set(resSpec, []); // ensure entry exists
            const atomNames = resMap.get(resSpec);
            const distance = pointDistance(atom1, atom2);
            const clashDistance = getClashingDistance(atom1, atom2, tolerance);
            atomNames.push(`${atom2.atom} (${atom2.amber}) ${distance.toFixed(5)} < ${clashDistance.toFixed(5)}`);
        }
        let clashingAtomMsg = '';
        for (const [atom1, resMap] of atomResMaps.entries()) {
            for (const [resSpec, atomNames] of resMap.entries()) {
                clashingAtomMsg += `    ${atom1.atom} (${atom1.amber}) with ${resSpec}: ${atomNames.join('; ')}\n`;
            }
        }
        clashingExtraInfo.push(`Clashing Compound: ${cmpd.resSpec}, Clashing Atoms (clash distances include tolerance of ${tolerance}:\n${clashingAtomMsg}`);
    }
    return `${clashingExtraInfo.join('')}`;
}

async function doSelectAtom(atom, args) {
    if (atom) {
        const { connector } = App.getDataParents(atom);
        await doWithActiveSolute(atom, () => connector.cmdSelectAtom(atom, args));
        return;
    }

    // Null atom = background click. Deselect for all connections / caseDatas
    await Promise.all(
        App.Workspace.allCaseData().map(async (caseData) => {
            const { connector } = App.getDataParents(caseData);
            await doWithActiveSolute(caseData, () => connector.cmdSelectAtom());
        })
    );
}

async function selectAtom(atomIn, args) {
    const [first, ...rest] = ensureArray(atomIn);
    await doSelectAtom(first, args);

    // Server API doesn't support selecting multiple atoms, so we achieve this by setting the
    // control key for every atom after the first. But need to make sure they aren't already
    // selected, since the ctrl key will make the attempt to select them actually deselect!
    if (rest.length > 0) {
        const restArgs = args ? { ...args } : {};
        restArgs.ctrlKey = true;

        await withPausedRedisplay((async () => {
            // The shift key may cause other atoms to become selected, so check each atom's
            // selection state before sending the select cmd, avoiding accidental deselection.
            for (const atom of rest) {
                if (!App.Workspace.isSelectedAtom(atom)) {
                    await doSelectAtom(atom, restArgs);
                }
            }
        })()); // IIFE
    }
    postSelection();
}

/**
 * @param {string} query
 * @param {CaseData} [caseData]
 */
async function selectCmd(queryIn, caseData=null) {
    // Select is called with no case from the command interface (CmdInterpreter) and from
    // SelectBox when the protein picker is hidden. setSoluteForComponent used to supply
    // this fallback from its `if (!item)` branch; that branch now only skips the repoint,
    // so the connector has to be resolved here on the same condition.
    const connector = caseData ? App.getDataParents(caseData).connector : App.ServerConnection;
    let query = queryIn;
    if (!query || query === 'select ') {
        console.warn("Attempting to select a blank string. Selecting 'none' instead.");
        query = 'none';
    }
    await doWithActiveSolute(caseData, () => connector.cmdSelect(query));
    postSelection();
}

function postSelection() {
    EventBroker.publish('selectionDisplay', App.Workspace.getSelectedAtoms());
    EventBroker.publish('refreshAtomDisplay');
    EventBroker.publish('redisplayRequest');
}

async function exportCompoundsToFormat(compoundsIn, format, sdfOptions={}) {
    const compounds = [].concat(compoundsIn); // force array
    const result = { data: '', error: '' };
    function addError(error) { result.error += `${result.error ? '; ' : ''}${error}`; }

    switch (format) {
        case 'mol':
            result.data = compounds[0] ? compounds[0].getMol2000() : '';
            return result;
        case 'sdf':
            result.data = Compound.makeSDF(compounds, sdfOptions);
            return result;
        case 'moljson': {
            // Converted on the client: the bfd-server exports via OpenBabel, which has no
            // MolJSON writer. See util/moljson_export.js.
            const { data, error } = compoundsToMolJson(compounds);
            result.data = data;
            if (error) addError(error);
            return result;
        }
        case 'smi':
            result.data = compounds.map((c) => c.getSmiles()).join('\n');
            return result;
        case 'smiNoName':
            result.data = compounds.map((c) => c.getUnnamedSmiles()).join('\n');
            return result;
        case 'inchikey':
        case 'inchi':
            if (compounds.every((c) => c.getProperty('structure2d', format))) {
                result.data = compounds.map((c) => c.getProperty('structure2d', format)).join('\n');
                return result;
            }
            // fallthrough to call server if inchikey isn't already available locally.
        default:
            // passthrough
    }

    const allowedMultiProteinFormats = ['sdf', 'smi', 'csv', 'mol2', 'xyz'];

    const { dataParentsMap, dataParentsList } = App.partitionByDataParents(compounds);
    if (dataParentsList.length > 1 && !allowedMultiProteinFormats.includes(format)) {
        addError(`Compounds from multiple proteins cannot be exported to format ${format}.`);
        return result;
    }

    for (const [{ caseData }, cmpds] of dataParentsMap.entries()) {
        const query = cmpds.map((c) => c.selectQuery).join(' or ');
        const { data, error } = await exportQueryToFormat(query, format, caseData);
        if (data) result.data += data;
        if (error) addError(error);
    }

    return result;
}

async function exportSelectionToFormat(formatIn, caseData) {
    // This is the single choke point for server-side export, so the invariant that a
    // client-only format never reaches the bfd-server is enforced here rather than
    // trusting every caller. MolJSON also needs whole compounds -- it reads each one's
    // molblock -- which an arbitrary atom selection does not provide.
    if (isClientOnlyExportFormat(formatIn)) {
        return {
            data: null,
            error: `${formatIn.toUpperCase()} export is not available for a 3D atom `
                + 'selection. Please click the background of the 3D workspace to deselect '
                + 'any atoms, then use the left-hand compound selector to choose which '
                + 'compounds to export.',
        };
    }

    // caseData is optional here too; same fallback as selectCmd.
    const connector = caseData ? App.getDataParents(caseData).connector : App.ServerConnection;
    const smiNoNameSupported = connector.getMode() === 'static';
    let format = formatIn;
    if (format === 'smiNoName' && !smiNoNameSupported) format = 'smi';

    const result = { data: null, error: null };

    try {
        result.data = await doWithActiveSolute(
            caseData, () => connector.cmdExportSelection(format)
        );
    } catch (err) {
        result.error = err;
    }

    return result;
}

/**
 * @param {string} query
 * @param {'mol'|'smi'|'smiNoName'|'sdf'|'xyz'|'csv'} format
 * @param {CaseData} [caseData]
 * @returns {Promise<{data: string, error: string?}>}
 */
async function exportQueryToFormat(query, format, caseData) {
    let result = { data: null, error: null };
    const needToSelect = (query && query !== 'selection');
    try {
        if (needToSelect) {
            await UserActions.Select(query, caseData);
        }

        result = await exportSelectionToFormat(format, caseData);
    } catch (err) {
        result.error = err;
    }

    if (needToSelect) {
        await UserActions.Select('none', caseData);
    }
    return result;
}

/**
 * Export atoms to format.  The atoms have to already have been selected.
 */
async function exportSelectedAtomsToFormat(atoms, format) {
    if (atoms.length === 0) {
        return { error: 'No atoms available for export' };
    }

    const { caseData } = App.getDataParents(atoms[0]);
    if (!atoms.every((atom) => App.getDataParents(atom).caseData === caseData)) {
        return { error: 'Selected atoms from only one protein can be exported. Try selecting only atoms on one protein.' };
    }

    return exportSelectionToFormat(format, caseData);
}

/**
 * Export the given spec (compounds, atoms, or query string) to the given format
 * @param {{ compounds: Compound[]?, atoms: Atom[]?, query: string? }} exportSpec
 * @param {string} format
 * @param {CaseData} [caseData]
 */
async function exportToFormat(exportSpec, format, caseData) {
    const { compounds=[], atoms=[], query } = exportSpec || {};
    if (compounds.length > 0) {
        return exportCompoundsToFormat(compounds, format);
    } else if (atoms.length > 0) {
        return exportSelectedAtomsToFormat(atoms, format);
    } else if (query) {
        return exportQueryToFormat(query, format, caseData);
    } else {
        return { error: 'Nothing specified for export' };
    }
}

async function loadStarterCompounds(caseData) {
    const { connector } = App.getDataParents(caseData);
    const responseData = await doWithActiveSolute(
        caseData, () => connector.cmdLoadStarterCompounds()
    );
    const loadedCompounds = responseData.byId(ResponseIds.Compound);
    const loadErrors = responseData.byId(ResponseIds.Error);
    App.Workspace.addReplaceCompounds(loadedCompounds, caseData);
    integrateCompounds(responseData, caseData);
    integrateEnergies(responseData, caseData);
    handleClashingCompounds(loadedCompounds);
    App.Workspace.activateFirstOf(loadedCompounds);
    caseData.getSampleCompoundInfo().setLoaded();
    CompoundHeritage.addStarterCompounds(loadedCompounds, PdbImportCase.isValidId);
    return { compounds: loadedCompounds, errors: loadErrors };
}

/**
 * Refresh a single compound's molecule properties, repointing the active solute to its
 * case first. get-molecule-properties carries no case identity of its own (BP2-2638), so a
 * caller that may be asked to fetch properties for compounds across more than one loaded
 * case (e.g. the Inspector, which awaits this once per visible compound) needs to repoint
 * before each request. Compound.updateMolProps() has no repoint of its own - see the comment
 * there - so every caller scopes it this way, including the un-awaited bulk fan-out in
 * integrateCompounds, which uses the same helper.
 * @param {Compound} compound
 */
async function updateMolProps(compound) {
    // Compound.updateMolProps obeys the doWithActiveSolute rule that the server command is the
    // first await in the function.
    await doWithActiveSolute(compound, () => compound.updateMolProps());
}

async function removeCompound(compound) {
    const { connector, caseData } = App.getDataParents(compound);
    App.Workspace.removeCompound(compound);
    await doWithActiveSolute(caseData, () => connector.cmdRemoveCompound(compound));
}

async function renameCompound(compound, newName) {
    const { connector } = App.getDataParents(compound);
    const responseData = await doWithActiveSolute(
        compound, () => connector.cmdRenameCompound(compound, newName)
    );
    if (responseData.hasError()) {
        console.log(`Failed to rename compound: ${responseData.errors.join('; ')}`);
        const error = responseData.errors.join(';');
        if (error.includes('rename-compound command attempted to rename to an existing compound')) {
            showAlert('Rename Failed', `Failed to rename compound: ${newName} is already the name of another compound`);
        } else if (error.includes('rename-compound command attempted to rename a co-crystal ligand')) {
            showAlert('Rename Failed', 'Failed to rename compound: co-crystal ligands cannot be renamed');
        } else {
            showAlert('Rename Failed', 'There was a problem renaming the compound');
        }
        return { errors: responseData.errors };
    } else {
        App.Workspace.renameCompound(compound, newName);
        return { errors: [] };
    }
}

async function copyCompound(compound) {
    const { connector, caseData } = App.getDataParents(compound);
    const responseData = await doWithActiveSolute(
        compound, () => connector.cmdCopyCompound(compound)
    );
    if (responseData.hasError()) {
        console.log(`Failed to copy compound: ${responseData.errors.join('; ')}`);
        return { compounds: [], errors: responseData.errors };
    } else {
        const compounds = responseData.byId(ResponseIds.Compound);
        App.Workspace.addReplaceCompounds(compounds, caseData);
        integrateCompounds(responseData, caseData);
        for (const c of compounds) {
            console.log(`Copied compound: ${compound.resSpec} -> ${c.resSpec}`);
            c.copyScopedPropertiesFrom(compound, { operation: 'duplicate' });
        }
        App.Workspace.activateFirstOf(compounds);
        CompoundHeritage.addDuplicate(compound, compounds);
        return { compounds, errors: [] };
    }
}

async function copyResiduesToCompound(residuesIn) {
    const residues = ensureArray(residuesIn);
    if (residues.length === 0) {
        return { compounds: [], errors: [] };
    }
    const { dataParentsList } = App.partitionByDataParents(residues);
    if (dataParentsList.length > 1) {
        return { compounds: [], errors: ["Can't copy residues from different structures to a compound"] };
    }
    const { connector, caseData } = App.getDataParents(residues[0]);
    const responseData = await doWithActiveSolute(
        residues[0], () => connector.cmdCopyCompound(residues)
    );
    if (responseData.hasError()) {
        console.log(`Failed to copy residues: ${responseData.errors.join('; ')}`);
        return { compounds: [], errors: responseData.errors };
    } else {
        const compounds = responseData.byId(ResponseIds.Compound);
        App.Workspace.addReplaceCompounds(compounds, caseData);
        integrateCompounds(responseData, caseData);
        App.Workspace.activateFirstOf(compounds);
        return { compounds, errors: [] };
    }
}

async function removeConnection(connector) {
    if (App.DataConnections.length === 1) {
        // Primary data connection should be zapped instead of removed
        await zapAll();
        return;
    }
    await App.ConnectionManager.removeConnector(connector);
}

/* Helpers */
function applyCaseProperties(caseData) {
    if (!caseData) return;
    const { mapCase } = App.getDataParents(caseData);
    if (mapCase instanceof FoldingDataImportCase) {
        applyFoldingResultProperties(caseData);
    }
}

function applyFoldingResultProperties(caseData) {
    const { mapCase } = App.getDataParents(caseData);
    const resProps = mapCase.resultProperties;
    if (!resProps) {
        return;
    }

    if (['Boltz-1', 'Boltz-2'].includes(mapCase.foldingData.foldingProgramId)) {
        if (resProps.confidence?.confidence_score != null) {
            mapCase.setProperty('boltz', 'folding_confidence', resProps.confidence.confidence_score);
        }

        const boltzAffinityBinder = extractBoltzAffinityBinder(mapCase, caseData);
        if (boltzAffinityBinder) {
            if (!resProps.affinity) {
                console.warn(`Affinity requested for ${boltzAffinityBinder} but no affinity data found in folding results. Case ${mapCase.displayName}`);
            }

            // Only one compound should have this chain.
            const cmpd = caseData.getCompounds().find((c) => c.chain === boltzAffinityBinder);
            if (cmpd) {
                cmpd.setProperty('boltz', 'binding_affinity', resProps.affinity?.affinity_pred_value ?? null);
                cmpd.setProperty('boltz', 'binding_probability', resProps.affinity?.affinity_probability_binary ?? null);
            } else {
                mapCase.setProperty('boltz', 'other_affinity_binder', boltzAffinityBinder);
                mapCase.setProperty('boltz', 'other_binding_affinity', resProps.affinity?.affinity_pred_value ?? null);
                mapCase.setProperty('boltz', 'other_binding_probability', resProps.affinity?.affinity_probability_binary ?? null);
            }
        }
    }
}

/**
 * Extract the affinity binder chain from the folding request args.
 * Currently just looks at the json payload, but could be updated to look at the "exact data"
 * FASTA or YAML.
 *
 * Handles the special case for screening results.
 * @param {FoldingDataImportCase} mapCase
 * @param {CaseData} caseData
 * @returns {string?}
 */
function extractBoltzAffinityBinder(mapCase, caseData) {
    const configuredBinder = mapCase.foldingData.foldingArgs
        ?.boltz_args?.json?.properties?.[0]?.affinity?.binder;
    if (configuredBinder) return configuredBinder;

    // From here, we're dealing with a folding result from a screening job.
    // A screening-derived fold has no foldingArgs at all - the original Boltz request
    // config is built server-side (doBoltzScreen) and never reaches the client - so this
    // falls back to caseData's own compound when there's exactly one. That's not a guess:
    // screening always co-folds one compound per structure, so that compound unambiguously
    // IS the affinity binder, without needing the config to say so.
    // Consider communicating the folding args back from the server.
    const compounds = caseData.getCompounds();
    return compounds.length === 1 ? compounds[0].chain : undefined;
}

/**
 * Compute a structure's whole-protein USAlign transform to a reference frame
 * WITHOUT loading the structure into the 3D workspace.
 *
 * align-protein is a pure server command over two PDB strings, so we only need
 * the structure's coordinates, not a workspace entry. We load it server-side
 * (lazy → no fragment fetch), wrap the returned atom groups in a DETACHED
 * CaseData (never added to a collection / the workspace), serialize its first
 * chain to PDB client-side — the same conversion pi-pi detection uses
 * (getPdbDataForResidues) — align it to the reference's first-chain PDB, then
 * drop the server solute. Nothing enters the workspace model, so there is no
 * redisplay, no camera recenter, and no active-solute churn.
 *
 * Using the first protein chain mirrors defaultAlignmentSelection, so the
 * transform matches one produced by a normal keepExisting load + alignProtein.
 *
 * @param {MapCase} mapCase
 * @param {CaseData} refCaseData
 * @param {import('BMapsModel').DataConnection} dataConnection
 * @returns {Promise<{ transform: object?, referenceFrame: string? }>}
 */
async function computeUnloadedAlignment(mapCase, refCaseData, dataConnection) {
    const { connector } = dataConnection.get();

    // Load the structure on the server only (no handleProteinResponses/addProtein).
    const loadOptions = serverLoadOptions(mapCase, { fragmentLoading: 'skip' });
    const responseData = await loadCaseServerOnly(connector, mapCase, loadOptions);
    const [solute] = responseData.byId(ResponseIds.Solute);
    if (!solute) throw new Error(`no solute reported for ${mapCase?.uri}`);
    const { soluteName } = solute;

    try {
        // Detached CaseData: gives us getProteinChains()/residues + client-side PDB
        // without touching the workspace, firing events, or registering a target.
        const alignCaseData = new CaseData(mapCase);
        alignCaseData.addAtomGroups(solute.allAtomGroups);

        const pdbAlign = firstChainPdb(alignCaseData);
        const pdbRef = firstChainPdb(refCaseData);
        if (!pdbAlign) throw new Error(`no protein chain for ${mapCase?.uri}`);
        if (!pdbRef) throw new Error(`no protein chain for reference ${refCaseData.mapCase?.uri}`);

        // Server-side USAlign over the two client-generated PDB strings.
        const alignResponse = await connector.cmdAlignProtein({ pdbRef, pdbAlign });
        const [alignMatrix] = alignResponse.byId(ResponseIds.ProteinRotationMatrix);
        return {
            transform: alignMatrix?.result ?? null,
            referenceFrame: alignmentFrameKey(refCaseData),
        };
    } finally {
        // Drop the server solute we never integrated into the workspace, and leave
        // the active solute pointing at a live solute (the load made the removed
        // structure active server-side).
        await connector.cmdRemoveSolute(soluteName);
        if (refCaseData.soluteName) await connector.cmdSetActiveSolute(refCaseData.soluteName);
    }
}

/**
 * Serialize a caseData's first protein chain to a PDB string client-side,
 * mirroring defaultAlignmentSelection (first chain). Returns null if there is
 * no protein chain.
 * @param {CaseData} caseData
 * @returns {string?}
 */
function firstChainPdb(caseData) {
    const [firstChain] = caseData.getProteinChains();
    if (!firstChain) return null;
    return getPdbDataForResidues(firstChain.getResidues()).format();
}

/**
 * Send the appropriate server load command for a MapCase and return its raw
 * responseData, WITHOUT integrating the result into the workspace. Mirrors the
 * load branching in chooseProtein but stops at the connector call.
 * @param {*} connector
 * @param {MapCase} caseInfo
 * @param {*} loadOptions server-side load options (from serverLoadOptions)
 */
function loadCaseServerOnly(connector, caseInfo, loadOptions) {
    if (caseInfo instanceof PdbImportCase || caseInfo instanceof AlphaFoldImportCase) {
        return connector.cmdLoadPdbId({ pdbId: caseInfo.pdbID, loadOptions });
    }
    if (caseInfo instanceof UserDataImportCase) {
        return connector.cmdLoadPdbString({
            data: caseInfo.data,
            pdbId: caseInfo.pdbID,
            moleculeName: caseInfo.molecule_name,
            fileType: caseInfo.data_format,
            loadOptions,
        });
    }
    if (caseInfo instanceof FoldingDataImportCase) {
        return connector.cmdLoadPdbString({
            data: caseInfo.data,
            fileType: caseInfo.data_format,
            moleculeName: caseInfo.molecule_name,
            loadOptions,
        });
    }
    return connector.cmdGetCaseFiles(caseInfo.uri, loadOptions);
}
